import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { makeGeometries, H } from "./humanoid";

const GEO = makeGeometries({});
const GEO_ROBE = makeGeometries({ robe: true, legLen: 0.22 });

// A single hand-built (non-instanced) humanoid with an idle/walk cycle so a
// few standout characters feel individually human up close.
function HumanoidRig({
  geo = GEO,
  skin = "#e8b98e",
  torso = "#3a4a63",
  legColor = "#2f4a63",
  armColor,
  walk = 0.4,
  armRaiseL = 0,
  armRaiseR = 0,
  gestureR = 0,
  lean = 0.05,
  children,
}) {
  const legL = useRef();
  const legR = useRef();
  const armL = useRef();
  const armR = useRef();
  const torsoRef = useRef();
  const seed = useRef(Math.random() * 10);

  useFrame((s) => {
    const t = s.clock.elapsedTime + seed.current;
    const ph = t * (2 + walk * 4);
    const sw = Math.sin(ph) * (0.15 + walk * 0.5);
    if (legL.current) legL.current.rotation.x = sw;
    if (legR.current) legR.current.rotation.x = -sw;
    if (armL.current) armL.current.rotation.x = -sw * 0.7 - armRaiseL;
    if (armR.current)
      armR.current.rotation.x = sw * 0.7 - armRaiseR - (gestureR ? (Math.sin(t * 2) * 0.4 + 0.6) * gestureR : 0);
    if (torsoRef.current) {
      torsoRef.current.rotation.x = lean;
      torsoRef.current.rotation.z = Math.sin(t * 0.9) * 0.03;
    }
  });

  const a = armColor || torso;
  return (
    <group>
      <mesh ref={legL} position={[-H.legX, H.hipY, 0]} geometry={geo.leg} castShadow>
        <meshStandardMaterial color={legColor} roughness={0.75} />
      </mesh>
      <mesh ref={legR} position={[H.legX, H.hipY, 0]} geometry={geo.leg} castShadow>
        <meshStandardMaterial color={legColor} roughness={0.75} />
      </mesh>
      <mesh ref={torsoRef} position={[0, H.hipY, 0]} geometry={geo.torso} castShadow>
        <meshStandardMaterial color={torso} roughness={0.7} />
      </mesh>
      <mesh ref={armL} position={[-H.armX, H.shoulderY, 0]} geometry={geo.arm} castShadow>
        <meshStandardMaterial color={a} roughness={0.7} />
      </mesh>
      <mesh ref={armR} position={[H.armX, H.shoulderY, 0]} geometry={geo.arm} castShadow>
        <meshStandardMaterial color={a} roughness={0.7} />
      </mesh>
      <mesh position={[0, H.headY, 0]} geometry={geo.head} castShadow>
        <meshStandardMaterial color={skin} roughness={0.55} />
      </mesh>
      {children}
    </group>
  );
}

function Hero({ position, rotation = [0, 0, 0], scale = 0.42, ...props }) {
  return (
    <group position={position} rotation={rotation} scale={scale}>
      <HumanoidRig {...props} />
    </group>
  );
}

// ---- distinctive accessories (unit-humanoid space) --------------------------
const PaperTower = () => (
  <group position={[0, H.shoulderY - 0.05, 0.24]}>
    {Array.from({ length: 11 }).map((_, k) => (
      <mesh key={k} position={[Math.sin(k) * 0.01, 0.05 + k * 0.052, 0]} rotation={[0, k * 0.05, 0]} castShadow>
        <boxGeometry args={[0.24, 0.045, 0.3]} />
        <meshStandardMaterial color={k % 2 ? "#fdfdf5" : "#e7e7dd"} roughness={0.9} />
      </mesh>
    ))}
  </group>
);

const BigBackpack = () => (
  <group>
    <mesh position={[0, H.hipY + 0.28, -0.17]} castShadow>
      <boxGeometry args={[0.26, 0.34, 0.16]} />
      <meshStandardMaterial color="#7a2f2f" roughness={0.85} />
    </mesh>
    <mesh position={[0.18, H.hipY + 0.05, 0.1]} castShadow>
      <boxGeometry args={[0.12, 0.18, 0.14]} />
      <meshStandardMaterial color="#2b2b30" roughness={0.85} />
    </mesh>
  </group>
);

const Laptop = () => (
  <group position={[0, H.shoulderY - 0.14, 0.2]} rotation={[-0.35, 0, 0]}>
    <mesh castShadow>
      <boxGeometry args={[0.26, 0.02, 0.18]} />
      <meshStandardMaterial color="#c9ccd2" metalness={0.4} roughness={0.4} />
    </mesh>
    <mesh position={[0, 0.09, -0.09]} rotation={[-1.1, 0, 0]} castShadow>
      <boxGeometry args={[0.26, 0.02, 0.18]} />
      <meshStandardMaterial color="#1a1c22" emissive="#3a6ea5" emissiveIntensity={0.5} />
    </mesh>
  </group>
);

const Cap = () => (
  <group position={[0, H.headY + H.headR, 0]}>
    <mesh geometry={GEO_ROBE.cap} castShadow>
      <meshStandardMaterial color="#111318" roughness={0.5} />
    </mesh>
    <mesh position={[0.11, 0.01, 0.11]}>
      <sphereGeometry args={[0.02, 8, 8]} />
      <meshStandardMaterial color="#f5c14e" emissive="#ffcf5a" emissiveIntensity={0.8} metalness={1} roughness={0.3} />
    </mesh>
  </group>
);

const Briefcase = () => (
  <mesh position={[0.22, H.hipY + 0.02, 0.08]} castShadow>
    <boxGeometry args={[0.1, 0.16, 0.22]} />
    <meshStandardMaterial color="#1b1b1f" metalness={0.3} roughness={0.35} />
  </mesh>
);

const PaperCart = () => (
  <group position={[0, 0, 0.34]}>
    <mesh position={[0, 0.16, 0]} castShadow>
      <boxGeometry args={[0.34, 0.24, 0.28]} />
      <meshStandardMaterial color="#3a3a40" metalness={0.3} roughness={0.6} />
    </mesh>
    {[0, 1, 2].map((k) => (
      <mesh key={k} position={[0, 0.3 + k * 0.05, 0]} castShadow>
        <boxGeometry args={[0.3, 0.045, 0.24]} />
        <meshStandardMaterial color={k % 2 ? "#fdfdf5" : "#e7e7dd"} roughness={0.9} />
      </mesh>
    ))}
    <mesh position={[-0.13, 0.03, 0.1]} rotation={[Math.PI / 2, 0, 0]}>
      <cylinderGeometry args={[0.04, 0.04, 0.03, 12]} />
      <meshStandardMaterial color="#111" />
    </mesh>
    <mesh position={[0.13, 0.03, 0.1]} rotation={[Math.PI / 2, 0, 0]}>
      <cylinderGeometry args={[0.04, 0.04, 0.03, 12]} />
      <meshStandardMaterial color="#111" />
    </mesh>
  </group>
);

export default function HeroNPCs() {
  return (
    <group>
      {/* The Overloaded Adjunct — hauling a precarious tower of papers */}
      <Hero position={[1.7, 0.02, 2.5]} rotation={[0, -0.7, 0]} scale={0.46} skin="#c68642" torso="#2a9d8f" legColor="#33415a" armRaiseL={1.25} armRaiseR={1.25} walk={0.15} lean={0.14}>
        <PaperTower />
      </Hero>

      {/* The Commuter — overstuffed backpack + tote, hustling */}
      <Hero position={[-2.0, 0.02, 2.2]} rotation={[0, 0.6, 0]} scale={0.44} skin="#8d5524" torso="#d9a441" legColor="#2f4a63" walk={0.7} lean={0.12}>
        <BigBackpack />
      </Hero>

      {/* The Laptop Nomad — grading on the move */}
      <Hero position={[2.7, 0.02, 1.3]} rotation={[0, -1.0, 0]} scale={0.44} skin="#f6d3b0" torso="#455a64" legColor="#3b5b78" armRaiseL={1.4} armRaiseR={1.4} walk={0.2} lean={0.22}>
        <Laptop />
      </Hero>

      {/* The Cart Pusher — wheeling a load of ungraded work */}
      <Hero position={[-0.2, 0.02, 2.9]} rotation={[0, -0.15, 0]} scale={0.45} skin="#6b3f1d" torso="#7b4b8a" legColor="#41566b" armRaiseL={0.95} armRaiseR={0.95} walk={0.35} lean={0.16}>
        <PaperCart />
      </Hero>

      {/* The Lecturer — robed, mid-gesture at the tenure tier */}
      <Hero position={[1.35, 1.18, 1.35]} rotation={[0, -0.9, 0]} scale={0.5} geo={GEO_ROBE} skin="#e8b98e" torso="#0f2547" legColor="#14141a" walk={0.05} gestureR={1} lean={0.02}>
        <Cap />
      </Hero>

      {/* The Administrator — a suit moving through the crowd, briefcase in hand */}
      <Hero position={[-2.5, 0.02, 1.5]} rotation={[0, 0.8, 0]} scale={0.5} skin="#d69f6e" torso="#23262b" legColor="#23262b" walk={0.18} lean={0.03}>
        <Briefcase />
      </Hero>
    </group>
  );
}
